import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Crown, Sparkles, Users } from 'lucide-react';
import { Container } from '@/components/ui';
import { ROUTES } from '@/config/constants';

const TIERS = [
  {
    name: 'Free communities',
    price: '$0',
    note: 'Start with public or private groups.',
    icon: Users,
    features: ['Create a community', 'Invite members', 'Run discussions and wins'],
  },
  {
    name: 'Paid communities',
    price: '10%',
    note: 'WINR fee on paid memberships.',
    icon: Crown,
    features: ['Monetize monthly access', 'Keep your community branded', 'Manage members'],
  },
  {
    name: 'Creator tools',
    price: 'Included',
    note: 'Built for operators growing serious groups.',
    icon: Sparkles,
    features: ['WINR AI tools', 'Analytics-ready workflow', 'CRM and branded assets'],
  },
];

export function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-[linear-gradient(180deg,rgba(10,10,14,0.45),rgba(10,10,14,0.6))] backdrop-blur-[22px] backdrop-saturate-[1.3]"
      />
      <Container className="py-16 sm:py-20">
        <section className="mx-auto max-w-4xl text-center">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.42em] text-accent/90">
            Pricing
          </p>
          <h1 className="text-[42px] font-black leading-[0.98] tracking-[-1.5px] text-white sm:text-6xl">
            Build free. Monetize when you are ready.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base font-semibold leading-7 text-white/68 sm:text-lg">
            WINR keeps pricing simple for communities: launch without friction, then turn on paid
            access when your group is ready to become a business.
          </p>
        </section>

        <section className="mt-12 grid gap-4 lg:grid-cols-3">
          {TIERS.map(({ name, price, note, icon: Icon, features }) => (
            <article
              key={name}
              className="glass-elevated flex min-h-[310px] flex-col rounded-[28px] p-6"
            >
              <div className="mb-7 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.08] text-accent">
                <Icon size={20} strokeWidth={2.2} />
              </div>
              <h2 className="text-xl font-black text-white">{name}</h2>
              <div className="mt-4 text-5xl font-black tracking-[-1.5px] text-white">{price}</div>
              <p className="mt-3 min-h-[44px] text-sm font-semibold leading-6 text-white/58">
                {note}
              </p>
              <div className="mt-7 space-y-3">
                {features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-3 text-sm font-bold text-white/76"
                  >
                    <Check size={16} strokeWidth={2.4} className="text-accent" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </section>

        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => navigate(ROUTES.home)}
            className="inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-sm font-black text-black shadow-[0_18px_45px_rgba(0,0,0,0.28)] transition-transform hover:-translate-y-0.5"
          >
            Explore communities
            <ArrowRight size={17} strokeWidth={2.4} />
          </button>
        </div>
      </Container>
    </div>
  );
}
